import { ArrowLeft } from "lucide-react";
import { useLanguage } from "@/lib/language-context";
import { useApp } from "@/lib/app-context";
import { Button } from "@/components/ui/button";
import type { Language } from "@/lib/i18n";

export function Header() {
  const { language, setLanguage, t } = useLanguage();
  const { currentScreen, setCurrentScreen } = useApp();

  const showBackButton = currentScreen !== "home";

  const handleBack = () => {
    if (currentScreen === "apply-country") {
      setCurrentScreen("home");
    } else if (currentScreen === "apply-passport") {
      setCurrentScreen("apply-country");
    } else if (currentScreen === "apply-payment") {
      setCurrentScreen("apply-passport");
    } else if (currentScreen === "apply-success") {
      setCurrentScreen("home");
    } else if (currentScreen === "status" || currentScreen === "faq") {
      setCurrentScreen("home");
    }
  };

  const languageLabels: Record<Language, string> = {
    en: "English",
    si: "සිංහල",
    ta: "தமிழ்",
  };

  // Short labels for the segmented buttons
  const languageShort: Record<Language, string> = {
    en: "EN",
    si: "සි",
    ta: "த",
  };

  return (
    <header className="sticky top-0 z-50 bg-background border-b border-border">
      <div className="flex items-center justify-between px-4 py-3">
        {/* Left - Back button or Logo */}
        <div className="flex items-center gap-3">
          {showBackButton ? (
            <Button
              variant="ghost"
              size="lg"
              onClick={handleBack}
              className="h-12 w-12 p-0 text-primary hover:bg-secondary"
              aria-label={t.back}
            >
              <ArrowLeft className="h-6 w-6" />
            </Button>
          ) : (
            <button
              onClick={() => setCurrentScreen("home")}
              className="flex items-center gap-2"
              aria-label="Go to home"
            >
              <img
                src="/images/logo.png"
                alt="Vimana Travels Logo"
                className="h-10 w-auto"
              />
            </button>
          )}

          {!showBackButton && (
            <div className="flex flex-col">
              <span className="text-lg font-bold text-primary leading-tight">VIMANA</span>
              <span className="text-xs text-muted-foreground">Travels</span>
            </div>
          )}
        </div>

        {/* Right - Language Selector - Always visible segmented buttons */}
        <div className="flex items-center border-2 border-primary/20 rounded-xl overflow-hidden">
          {(["en", "si", "ta"] as Language[]).map((lang) => (
            <button
              key={lang}
              onClick={() => setLanguage(lang)}
              className={`px-3 py-2 text-base font-semibold transition-colors min-w-[44px] ${
                language === lang
                  ? "bg-primary text-primary-foreground"
                  : "bg-transparent text-primary hover:bg-secondary"
              }`}
              aria-label={languageLabels[lang]}
              aria-pressed={language === lang}
            >
              {languageShort[lang]}
            </button>
          ))}
        </div>
      </div>
    </header>
  );
}
